import { useCallback, useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import LoadingSpinner from '@components/common/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import {
  crawlConfigService,
  type CrawlConfig,
  type CrawlConfigMeta,
} from '@services/crawlConfigService';

export interface ContextCrawlConfigSectionProps {
  orgId: string;
  contextId: string;
}

const DEFAULT_META: CrawlConfigMeta = {
  maxSettleQuietMs: 10000,
  maxSettleTimeoutMs: 120000,
  defaultHashRoutes: false,
};

/** A number field's string state, so "clear it to inherit the default" works. */
const toField = (value: number | undefined): string =>
  typeof value === 'number' ? String(value) : '';

/**
 * REV-1169: per-tracked-place crawl render options on the context Settings tab.
 * A single-page app that routes on the URL fragment (`/#/about`) looks like one
 * page to a crawler; opting into hash routes crawls it as the pages it has, and
 * the render-settle window gives a client-rendered page time to paint before
 * its links are read. A field left blank inherits afixt-spider's own default.
 */
export default function ContextCrawlConfigSection({
  orgId,
  contextId,
}: ContextCrawlConfigSectionProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const settleGroupId = useId();

  const [meta, setMeta] = useState<CrawlConfigMeta>(DEFAULT_META);
  const [hashRoutes, setHashRoutes] = useState(false);
  const [quiet, setQuiet] = useState('');
  const [timeout, setTimeoutMs] = useState('');
  const [errors, setErrors] = useState<{ quiet?: string; timeout?: string }>({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { config, meta: m } = await crawlConfigService.getContextCrawlConfig(orgId, contextId);
      setMeta(m ?? DEFAULT_META);
      setHashRoutes(Boolean(config.hashRoutes));
      setQuiet(toField(config.settleQuietMs));
      setTimeoutMs(toField(config.settleTimeoutMs));
    } catch {
      setLoadError(t('crawlConfig.errors.loadFailed', 'Could not load the crawl settings.'));
    } finally {
      setLoading(false);
    }
  }, [orgId, contextId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Validate on submit (never pre-disable the button): parse the two optional
  // durations, range-check them, and reject a quiet window longer than the
  // timeout. Returns the config to save, or null when something is wrong.
  const validate = (): CrawlConfig | null => {
    const next: { quiet?: string; timeout?: string } = {};
    const config: CrawlConfig = { hashRoutes };

    const parse = (raw: string, max: number, key: 'quiet' | 'timeout'): number | undefined => {
      const trimmed = raw.trim();
      if (trimmed === '') return undefined;
      const n = Number(trimmed);
      if (!Number.isInteger(n) || n < 0 || n > max) {
        next[key] = t('crawlConfig.errors.range', {
          defaultValue: 'Enter a whole number of milliseconds from 0 to {{max}}.',
          max,
        });
        return undefined;
      }
      return n;
    };

    const q = parse(quiet, meta.maxSettleQuietMs, 'quiet');
    const to = parse(timeout, meta.maxSettleTimeoutMs, 'timeout');
    if (q !== undefined) config.settleQuietMs = q;
    if (to !== undefined) config.settleTimeoutMs = to;

    if (!next.quiet && q !== undefined && to !== undefined && to > 0 && q > to) {
      next.quiet = t('crawlConfig.errors.quietAboveTimeout', {
        defaultValue: 'The quiet window cannot be longer than the timeout.',
      });
    }

    setErrors(next);
    return Object.keys(next).length === 0 ? config : null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaved(false);
    setSaveError(null);
    const config = validate();
    if (!config) return;

    setSaving(true);
    try {
      const updated = await crawlConfigService.putContextCrawlConfig(orgId, contextId, config);
      setHashRoutes(Boolean(updated.hashRoutes));
      setQuiet(toField(updated.settleQuietMs));
      setTimeoutMs(toField(updated.settleTimeoutMs));
      setSaved(true);
    } catch {
      setSaveError(t('crawlConfig.errors.saveFailed', 'Could not save the crawl settings.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="section" aria-labelledby={headingId}>
      <Typography id={headingId} variant="h6" component="h2" gutterBottom>
        {t('crawlConfig.heading', 'Crawl settings')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'crawlConfig.description',
          'How this place is crawled. These apply to whole-site crawls of single-page apps.'
        )}
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" role="status" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
          {t('crawlConfig.saved', 'Crawl settings saved.')}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <LoadingSpinner size={24} />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <FormGroup sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={hashRoutes}
                  onChange={(e) => setHashRoutes(e.target.checked)}
                  inputProps={{ 'aria-describedby': `${headingId}-hash-help` }}
                />
              }
              label={t('crawlConfig.hashRoutes.label', 'Crawl hash routes as separate pages')}
            />
            <Typography
              id={`${headingId}-hash-help`}
              variant="caption"
              color="text.secondary"
              sx={{ ml: 6, display: 'block' }}
            >
              {t(
                'crawlConfig.hashRoutes.help',
                'For single-page apps that route on the URL fragment, e.g. example.com/#/about. Off by default.'
              )}
            </Typography>
          </FormGroup>

          <Typography id={settleGroupId} variant="subtitle2" component="h3" gutterBottom>
            {t('crawlConfig.settle.heading', 'Render-settle window')}
          </Typography>
          <Box
            role="group"
            aria-labelledby={settleGroupId}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}
          >
            <TextField
              type="number"
              label={t('crawlConfig.settleQuiet.label', 'Quiet window (ms)')}
              value={quiet}
              onChange={(e) => setQuiet(e.target.value)}
              error={Boolean(errors.quiet)}
              helperText={
                errors.quiet ??
                t('crawlConfig.settleQuiet.help', {
                  defaultValue:
                    'How long the page must be quiet before links are read. 0–{{max}}. Blank uses the default.',
                  max: meta.maxSettleQuietMs,
                })
              }
              inputProps={{ min: 0, max: meta.maxSettleQuietMs, step: 100 }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              type="number"
              label={t('crawlConfig.settleTimeout.label', 'Settle timeout (ms)')}
              value={timeout}
              onChange={(e) => setTimeoutMs(e.target.value)}
              error={Boolean(errors.timeout)}
              helperText={
                errors.timeout ??
                t('crawlConfig.settleTimeout.help', {
                  defaultValue:
                    'The most time to wait for the page to settle. 0–{{max}}. Blank uses the default.',
                  max: meta.maxSettleTimeoutMs,
                })
              }
              inputProps={{ min: 0, max: meta.maxSettleTimeoutMs, step: 500 }}
              sx={{ minWidth: 220 }}
            />
          </Box>

          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('crawlConfig.save', 'Save crawl settings')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
